import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  Search,
  Bell,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { InitialsAvatar } from "@/components/common/InitialsAvatar";
import { Money } from "@/components/common/Money";
import { MatchCard } from "@/components/coupon/MatchCard";
import { useAuth } from "@/hooks/useAuth";
import { useActiveSubscription } from "@/hooks/useSubscription";
import { useUserCoupons } from "@/hooks/useCoupons";
import { useNotifications } from "@/hooks/useNotifications";
import { useFeaturedMatches } from "@/hooks/useMatches";
import { useReferralStats } from "@/hooks/useReferral";
import { useEarnings } from "@/hooks/useEarnings";
import { useUi } from "@/stores/ui";
import { useCouponCart } from "@/stores/couponCart";
import { differenceInDays, parseISO } from "date-fns";
import { PLANS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function Home() {
  const { profile } = useAuth();
  const sub = useActiveSubscription();
  const coupons = useUserCoupons();
  const notifs = useNotifications();
  const featured = useFeaturedMatches();
  const referralStats = useReferralStats();
  const earnings = useEarnings();
  const { setCouponSheetOpen } = useUi();
  const cartType = useCouponCart((s) => s.type);

  const expiresIn = sub.data
    ? Math.max(0, differenceInDays(parseISO(sub.data.expires_at), new Date()))
    : 0;
  const planMeta = sub.data ? PLANS[sub.data.plan] : null;
  const unread = (notifs.data ?? []).filter((n) => !n.read_at).length;
  const pendingCoupons = (coupons.data ?? []).filter((c) => c.status === "pending").length;

  return (
    <div className="space-y-5 pb-2">
      {/* Top bar */}
      <header className="flex items-center justify-between pt-2">
        <Link to="/profile" className="flex items-center gap-2.5">
          <InitialsAvatar name={profile?.first_name} seed={profile?.id} size="sm" />
          <div>
            <p className="text-2xs text-muted-foreground leading-tight">Bonjour</p>
            <p className="text-sm font-semibold leading-tight">{profile?.first_name ?? "—"}</p>
          </div>
        </Link>
        <div className="flex items-center gap-1">
          <Button asChild variant="ghost" size="icon-sm" aria-label="search">
            <Link to="/matches"><Search className="size-[18px]" strokeWidth={1.75} /></Link>
          </Button>
          <Button asChild variant="ghost" size="icon-sm" className="relative" aria-label="alerts">
            <Link to="/notifications">
              <Bell className="size-[18px]" strokeWidth={1.75} />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-primary" />
              )}
            </Link>
          </Button>
        </div>
      </header>

      {/* Wallet card — fintech style */}
      <section className="rounded-2xl border border-white/[0.06] bg-card p-5 shadow-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">
              Solde wallet
            </p>
            <p className="mt-1 text-[34px] leading-none font-bold num tracking-tight">
              <Money amount={profile?.wallet_balance ?? 0} />
            </p>
            <div className="mt-1.5 flex items-center gap-1.5 text-2xs text-success">
              <TrendingUp className="size-3" strokeWidth={2.5} />
              <span className="font-medium num">+ <Money amount={earnings.total} /> total gagné</span>
            </div>
          </div>
          {planMeta && (
            <Link
              to="/plans"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 border border-primary/20 px-2 h-7 text-2xs font-semibold text-primary hover:bg-primary/15 transition-colors"
            >
              {planMeta.name}
              <span className="text-primary/60">·</span>
              <span className="num">{expiresIn}j</span>
            </Link>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button asChild size="default" className="w-full">
            <Link to="/wallet/deposit"><ArrowDownLeft className="size-3.5" /> Déposer</Link>
          </Button>
          <Button asChild size="default" variant="secondary" className="w-full">
            <Link to="/wallet/withdraw"><ArrowUpRight className="size-3.5" /> Retirer</Link>
          </Button>
        </div>
      </section>

      {/* No plan prompt */}
      {!sub.data && (
        <Link to="/plans" className="block">
          <div className="flex items-center gap-3 rounded-xl bg-card border border-white/[0.06] px-4 py-3 hover:border-white/[0.1] transition-colors">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Souscris à un plan</p>
              <p className="text-2xs text-muted-foreground mt-0.5">Active tes coupons quotidiens dès 1 000 FCFA</p>
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
          </div>
        </Link>
      )}

      {/* Quick stats grid (Apple Sports style) */}
      <section className="grid grid-cols-3 gap-2">
        <Stat label="Coupons" value={coupons.data?.length ?? 0} hint={pendingCoupons > 0 ? `${pendingCoupons} en cours` : "—"} />
        <Stat
          label="Parrainés"
          value={referralStats.total}
          hint={referralStats.paid > 0 ? `${referralStats.paid} abonné${referralStats.paid > 1 ? "s" : ""}` : "invités"}
        />
        <Stat label="Gains" value={<Money amount={earnings.total} compact />} hint="cumul" highlight />
      </section>

      {/* Featured matches */}
      <section className="space-y-3">
        <div className="flex items-center justify-between px-0.5">
          <h2 className="text-base font-semibold tracking-tight">À l'affiche</h2>
          <Link to="/matches" className="text-2xs font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-0.5">
            Tout voir <ChevronRight className="size-3" />
          </Link>
        </div>
        {(featured.data?.length ?? 0) > 0 ? (
          <div className="space-y-2">
            {featured.data!.slice(0, 3).map((m) => (
              <MatchCard key={m.id} match={m} />
            ))}
          </div>
        ) : (
          <EmptyTile message="Aucun match vedette" />
        )}
      </section>

      {/* My coupons */}
      <section className="space-y-3">
        <div className="flex items-center justify-between px-0.5">
          <h2 className="text-base font-semibold tracking-tight">Mes coupons</h2>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setCouponSheetOpen(true)}
            disabled={!!cartType}
          >
            <Plus className="size-3.5" /> Nouveau
          </Button>
        </div>
        {coupons.data && coupons.data.length > 0 ? (
          <div className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06]">
            {coupons.data.slice(0, 4).map((c) => (
              <Link key={c.id} to={`/coupon/${c.id}`} className="block px-4 py-3 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-3">
                  <CouponTypeTag type={c.type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{c.type}</p>
                    <p className="text-2xs text-muted-foreground">
                      {c.correct_count} pronostic{c.correct_count !== 1 ? "s" : ""} correct{c.correct_count !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <CouponStatusTag status={c.status} />
                  {c.payout_amount > 0 && (
                    <span className="text-sm font-semibold text-success num">
                      + <Money amount={c.payout_amount} compact />
                    </span>
                  )}
                  <ChevronRight className="size-4 text-muted-foreground" />
                </div>
              </Link>
            ))}
            {coupons.data.length > 4 && (
              <Link to="/history" className="flex items-center justify-center gap-1 py-3 text-2xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                Voir tout l'historique <ChevronRight className="size-3" />
              </Link>
            )}
          </div>
        ) : (
          <EmptyTile
            message="Pas de coupon actif"
            cta={
              <Button onClick={() => setCouponSheetOpen(true)} size="sm">
                <Plus className="size-3.5" /> Créer un coupon
              </Button>
            }
          />
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <div className={cn(
      "rounded-xl border border-white/[0.06] bg-card px-3 py-3",
      highlight && "bg-primary/[0.04] border-primary/20"
    )}>
      <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      <p className={cn("mt-1 text-xl font-semibold num", highlight && "text-primary")}>{value}</p>
      {hint && <p className="text-2xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function CouponTypeTag({ type }: { type: string }) {
  const config: Record<string, string> = {
    TOP10: "bg-primary/10 text-primary border-primary/20",
    TOP20: "bg-white/[0.06] text-foreground border-white/10",
    TOP30: "bg-white/[0.04] text-muted-foreground border-white/[0.08]",
  };
  return (
    <span className={cn(
      "inline-flex items-center justify-center min-w-[44px] h-7 rounded-md text-2xs font-semibold border tabular-nums px-2",
      config[type] ?? config.TOP30
    )}>
      {type}
    </span>
  );
}

function CouponStatusTag({ status }: { status: string }) {
  const cfg: Record<string, { label: string; cls: string }> = {
    winner: { label: "Gagné", cls: "text-success" },
    lost: { label: "Perdu", cls: "text-destructive" },
    refunded: { label: "Remb.", cls: "text-warning" },
    pending: { label: "En cours", cls: "text-muted-foreground" },
  };
  const m = cfg[status] ?? cfg.pending;
  return <span className={cn("text-2xs font-medium", m.cls)}>{m.label}</span>;
}

function EmptyTile({ message, cta }: { message: string; cta?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-white/[0.08] bg-card/30 px-5 py-8 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
      {cta && <div className="mt-3">{cta}</div>}
    </div>
  );
}
